import { Injectable } from '@angular/core';
import { OrganizationManager } from './organization.manager';
import { OrganizationLicenseStatusEnum } from '../enums/organization-license-status.enum';
import { LicenseStatusEnum } from '../../../../../../common/src/enums/license-status.enum';
import { differenceInDays } from 'date-fns';
import { LicenseModel } from '../../../../../../common/dist/angular/models/license.model';
import { BillingApiController } from '../api-controllers/billing.api-controller';
import { StripePaymentMethodModel } from '../../../../../../common/dist/angular/models/stripe-payment-method.model';
import { StripeInvoiceModel } from '../../../../../../common/dist/angular/models/stripe-invoice.model';

@Injectable()
export class BillingManager {
  constructor(
    private readonly organizationManager: OrganizationManager,
    private readonly billingApiController: BillingApiController,
  ) {
  }

  async getLicense(forceRefresh?: boolean): Promise<LicenseModel | undefined> {
    const organization = await this.organizationManager.getCurrent();

    if (organization === null || forceRefresh) {
      return (await this.organizationManager.refresh())?.activeLicense;
    }

    return organization.activeLicense;
  }

  async getLicenseStatus(forceRefresh?: boolean): Promise<OrganizationLicenseStatusEnum> {
    const activeLicense: LicenseModel = await this.getLicense();

    if (activeLicense === undefined) {
      return OrganizationLicenseStatusEnum.Unlicensed;
    }

    switch (activeLicense.status) {
      case LicenseStatusEnum.Active:
        return OrganizationLicenseStatusEnum.Active;
      case LicenseStatusEnum.Suspended:
        return OrganizationLicenseStatusEnum.Suspended;
      case LicenseStatusEnum.Trialing:
        if (activeLicense.trialEnd < new Date()) {
          return OrganizationLicenseStatusEnum.TrialEnded;
        }

        return OrganizationLicenseStatusEnum.Trialing;
    }
  }

  async getCycleEndDate(): Promise<Date | undefined> {
    const licenseModel = await this.getLicense();

    return licenseModel.currentCycleEnd;
  }

  /**
   * Number of days left until subscription is canceled. Handles both free trial and active without payment.
   * @param licenseModel
   */
  async getNumberOfDaysLeft(licenseModel?: LicenseModel): Promise<number> {
    if (licenseModel === undefined) {
      licenseModel = await this.getLicense();
    }

    let trialEnd = licenseModel.trialEnd;

    // todo: We should not need this. The transformer should transform it into the proper object. But eh, it's not doing it.
    if (typeof trialEnd === 'string') {
      trialEnd = new Date(trialEnd);
    }

    switch (licenseModel.status) {
      case LicenseStatusEnum.Trialing:
        return differenceInDays(trialEnd, new Date());

      case LicenseStatusEnum.Suspended:
        return 0;

      // If active, just say that it returns 30 days.
      case LicenseStatusEnum.Active:
        if (licenseModel.cancelAt === undefined) {
          return 30;
        }

        return differenceInDays(licenseModel.cancelAt, new Date());
    }
  }

  async getPaymentMethods(): Promise<StripePaymentMethodModel[]> {
    return this.billingApiController.getPaymentMethods();
  }

  async deletePaymentMethod(id: string): Promise<void> {
    return this.billingApiController.deletePaymentMethod(id);
  }

  async getInvoices(): Promise<StripeInvoiceModel[]> {
    return this.billingApiController.getInvoices();
  }
}
